import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './ChartFrame.module.css';

/**
 * The wrapper every chart on Estatísticas sits in (canvas 1l): title, a trailing figure or
 * legend on the same baseline, a subtitle that explains what the numbers are, and a sunken
 * framed body.
 *
 * The subtitle is not decoration. Two of the four charts on this screen would be actively
 * misleading without theirs — the handicap series is built from a value recorded at the
 * *start* of each round, and GIR and Fairway share an axis while having different
 * denominators. The frame makes that caption a required prop rather than something a screen
 * can forget.
 */
export function ChartFrame({
  title,
  aside,
  subtitle,
  children,
  xLabels,
  footnote,
  className,
}: {
  title: string;
  aside?: ReactNode;
  subtitle: string;
  children: ReactNode;
  /** Month markers under the plot, spread edge to edge. */
  xLabels?: string[];
  /** Explanatory line under the whole frame, outside the sunken box. */
  footnote?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx(styles.chart, className)}>
      <div className={styles.head}>
        <h3 className={styles.title}>{title}</h3>
        {aside != null && <div className={styles.aside}>{aside}</div>}
      </div>
      <p className={styles.subtitle}>{subtitle}</p>
      <div className={styles.body}>
        {children}
        {xLabels && xLabels.length > 0 && (
          <div className={styles.axis} aria-hidden="true">
            {xLabels.map((label, i) => (
              <span key={`${label}-${i}`}>{label}</span>
            ))}
          </div>
        )}
      </div>
      {footnote != null && <p className={styles.footnote}>{footnote}</p>}
    </section>
  );
}

/**
 * What a chart shows when it does not have enough data to be honest.
 *
 * Canvas 1l calls this "obrigatório em toda conta nova" and gives the reasoning: "Cada
 * gráfico declara seu mínimo. Com 2 voltas, uma linha de tendência mente — silêncio é mais
 * honesto." So this states the threshold *and* the current count: a user who is two rounds
 * away should be able to see that, not just be told no.
 */
export function ChartEmpty({
  requirement,
  have,
  shape = 'bars',
}: {
  requirement: string;
  have: string;
  shape?: 'bars' | 'dots' | 'line';
}) {
  return (
    <div className={styles.emptyBox}>
      <GhostShape shape={shape} />
      <p className={styles.emptyRequirement}>{requirement}</p>
      <p className={styles.emptyHave}>{have}</p>
    </div>
  );
}

function GhostShape({ shape }: { shape: 'bars' | 'dots' | 'line' }) {
  if (shape === 'dots') {
    return (
      <div className={styles.ghostDots} aria-hidden="true">
        <span />
        <span />
      </div>
    );
  }
  if (shape === 'line') {
    return (
      <svg className={styles.ghostLine} viewBox="0 0 120 34" aria-hidden="true">
        <polyline points="2,26 32,14 62,20 92,6 118,12" />
      </svg>
    );
  }
  return (
    <div className={styles.ghostBars} aria-hidden="true">
      <span style={{ height: '40%' }} />
      <span style={{ height: '70%' }} />
      <span style={{ height: '30%' }} />
      <span style={{ height: '55%' }} />
    </div>
  );
}

/**
 * The loading state, shaped like the chart it replaces.
 *
 * Canvas 1l: "O skeleton tem a forma do gráfico real e não é decoração — é a maior espera do
 * app." That is literally true here: with no aggregate endpoint, this screen walks every
 * round one request at a time, so `progress` reports how far along that walk is instead of
 * leaving the user watching a shimmer with no end in sight.
 */
export function ChartSkeleton({ progress }: { progress?: string }) {
  const bars = [46, 72, 58, 84, 64, 40];
  return (
    <div className={styles.skeletonBox}>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonBars}>
        {bars.map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} />
        ))}
      </div>
      {progress && <p className={styles.skeletonProgress}>{progress}</p>}
    </div>
  );
}
